import { observable, action } from 'mobx';
import moment from 'moment';
import api from '../api';
import { checkStatus } from '../util/fetchUtil';
const google = window.google;

export class MainStore {
    @observable activeRestaurantSearch;
    @observable averagePointsPerInspection;
    @observable averagePointsPerInspectionAllTime;
    @observable dateRange;
    @observable graphData;
    @observable graphDataAllTime;
    @observable hasMoreRestaurants;
    @observable hours;
    @observable loading;
    @observable mapObj;
    @observable openModal;
    @observable openNestedListItems;
    @observable pageNumber;
    @observable paginationLoading;
    @observable rating;
    @observable restaurants;
    @observable restaurantOffset;
    @observable restaurantPageSize;
    @observable restaurantSearchMode;
    @observable restaurantsSearchResults;
    @observable reviews;
    @observable searchLoading;
    @observable selectedRestaurant;
    @observable selectedRestaurantAllTime;
    @observable selectedRestaurantViolations;
    @observable selectedRestaurantViolationsAllTime;
    @observable selectedRestaurantViolationTypesAllTime;
    @observable showAllData;
    @observable showInfoWindow;
    @observable showReviews;
    @observable showSearch;
    @observable showSharingIcons;
    @observable violationTypeGraphData;
    @observable violationTypeGraphDataAllTime;

    constructor() {
        this.api = api;
        this.averagePointsPerInspection = 0;
        this.averagePointsPerInspectionAllTime = 0;
        this.dateRange = null;
        this.graphData = [];
        this.graphDataAllTime = [];
        this.hasMoreRestaurants = true;
        this.hours = [];
        this.loading = false;
        this.mapObj = null;
        this.openModal = observable.map();
        this.openNestedListItems = observable.map();
        this.pageNumber = 1;
        this.paginationLoading = false;
        this.rating = null;
        this.restaurants = [];
        this.restaurantOffset = 0;
        this.restaurantPageSize = 1000;
        this.restaurantsSearchResults = null;
        this.reviews = [];
        this.searchLoading = false;
        this.selectedRestaurant = null;
        this.selectedRestaurantViolations = observable.map();
        this.selectedRestaurantViolationsAllTime = observable.map();
        this.selectedRestaurantViolationTypesAllTime = observable.map();
        this.showAllData = false;
        this.showInfoWindow = false;
        this.showReviews = true;
        this.showSearch = false;
        this.showSharingIcons = false;
        this.violationTypeGraphData = [];
        this.violationTypeGraphDataAllTime = [];
    }

    @action filterData(arr) {
        let r = observable.map();
        while(arr.length) {
            arr.forEach((i, index) => {
                if(!r.has(i.business_id)) {
                    r.set(i.business_id, [i])
                } else {
                    r.set(i.business_id, [...r.get(i.business_id), i])
                }
                arr.splice(index, 1);
            });
        }
        let newArr = [];
        r.values().forEach((v) => {
            if(v.some(p => Number(p.violation_points) > 0)) {
                v.forEach((i) => {
                    newArr.push(i)
                })
            }
        });
        return newArr;
    }

    @action formatData(data) {
        return data.reduce((res, j) => {
            if (!res.some(res => res.business_id === j.business_id)) {
                res.push(j);
            }
            return res;
        }, []).map((j) => {
            return {
                id: j.business_id,
                name: j.name,
                address: j.address,
                city: j.city,
                zip_code: j.zip_code,
                phone: j.phone,
                inspection_date: j.inspection_date,
                inspection_closed_business: j.inspection_closed_business,
                inspection_serial_num: j.inspection_serial_num,
                violations: data.filter((el) => {
                    return el.business_id === j.business_id;
                }).map((el) => {
                    if(Number(el.violation_points > 0)) {
                        let desc = el.violation_description;
                        while (desc[desc.length - 1] === "." || desc[desc.length - 1] === ",") {
                            desc = desc.slice(0, -1);
                        }
                        return {
                            violation_date: moment(el.inspection_date).format('MM/DD/YYYY'),
                            violation_type: el.violation_type,
                            violation_description: desc.slice(7),
                            violation_points: Number(el.violation_points),
                        }
                    } else {
                        return {
                            violation_date: moment(el.inspection_date).format('MM/DD/YYYY'),
                            violation_type: 'no violations',
                            violation_description: '',
                            violation_points: Number(el.violation_points),
                        }
                    }
                }).sort((a,b) => new Date(a.violation_date) - new Date(b.violation_date))
            }
        }).sort((a, b) => b.violations.length - a.violations.length);
    }

    @action formatGraphData(pointsGraph, typeGraph, violations, violationTypes) {
        violations.forEach((value, key) => {
            pointsGraph.push({date: key, violation_points: value});
        });

        violationTypes.forEach((value, key) => {
            let red = value.filter(v => v.toUpperCase() === 'RED').length;
            let blue = value.filter(v => v.toUpperCase() === 'BLUE').length;
            typeGraph.push({date: key, red_violations: red, blue_violations: blue})
        });

        typeGraph = typeGraph.sort((a,b) => new Date(a.date) - new Date(b.date));
    }

    @action formatViolations(restaurant, violations, violationTypes) {
        restaurant.violations.forEach((v) => {
            if(!violations.has(v.violation_date)) {
                violations.set(v.violation_date, v.violation_points);
                violationTypes.set(v.violation_date, [v.violation_type]);
            } else {
                violations.set(v.violation_date, violations.get(v.violation_date) + v.violation_points);
                violationTypes.set(v.violation_date, [...violationTypes.get(v.violation_date), v.violation_type]);
            }
        });
    }

    @action fetchAllRestaurantRows(baseQuery) {
        const pageSize = 1000;
        const allRows = [];

        const fetchPage = offset => {
            const separator = baseQuery.length ? '&' : '';

            const query = `${baseQuery}${separator}$limit=${pageSize}&$offset=${offset}`;

            return this.api
                .getRestaurantListData(query)
                .then(checkStatus)
                .then(response => response.json())
                .then(rows => {
                    allRows.push(...rows);

                    if (rows.length === pageSize) {
                        return fetchPage(offset + pageSize);
                    }

                    return allRows;
                });
        };

        return fetchPage(0);
    }

    @action getRestaurantListData(isSearch, searchQuery, loadMore = false) {
        const pageSize = this.restaurantPageSize;

        const effectiveIsSearch = loadMore
            ? this.restaurantSearchMode
            : Boolean(isSearch);

        const effectiveSearchQuery = loadMore
            ? this.activeRestaurantSearch
            : (searchQuery || '');

        if (!loadMore) {
            this.restaurantOffset = 0;
            this.hasMoreRestaurants = true;
            this.restaurantSearchMode = effectiveIsSearch;
            this.activeRestaurantSearch = effectiveSearchQuery;

            if (effectiveIsSearch) {
                this.restaurantsSearchResults = [];
            } else {
                this.restaurants = [];
                this.restaurantsSearchResults = null;
            }

            this.loading = true;
        } else {
            this.paginationLoading = true;
        }

        if (!this.dateRange) {
            this.dateRange = this.setDateRange(12);
        }

        const now = moment()
            .toISOString()
            .slice(0, -1);

        const whereClause =
            `inspection_date between '${this.dateRange}' and '${now}'`;

        const queryParts = [
            `$limit=${pageSize}`,
            `$offset=${this.restaurantOffset}`,
            `$where=${encodeURIComponent(whereClause)}`,
            `city=${encodeURIComponent('SEATTLE')}`,
            `$order=${encodeURIComponent(
                'inspection_date DESC, inspection_serial_num ASC'
            )}`
        ];

        if (effectiveIsSearch && effectiveSearchQuery) {
            queryParts.push(
                `$q=${encodeURIComponent(effectiveSearchQuery)}`
            );
        }

        const query = queryParts.join('&');

        return this.api
            .getRestaurantListData(query)
            .then(checkStatus)
            .then(response => response.json())
            .then(action(json => {
                /*
                 * filterData mutates its input with splice(), so pass
                 * it a copy rather than the original API response.
                 */
                const filteredRows = this.filterData([...json]);
                const formattedRows = this.formatData(filteredRows);

                if (effectiveIsSearch) {
                    this.restaurantsSearchResults = loadMore
                        ? this.mergeRestaurants(
                            this.restaurantsSearchResults || [],
                            formattedRows
                        )
                        : formattedRows;
                } else {
                    this.restaurants = loadMore
                        ? this.mergeRestaurants(
                            this.restaurants,
                            formattedRows
                        )
                        : formattedRows;
                }

                /*
                 * Advance by the raw number of API records received,
                 * not the number left after filtering or formatting.
                 */
                this.restaurantOffset += json.length;

                /*
                 * If fewer than 1,000 rows were returned, we reached
                 * the final page.
                 */
                this.hasMoreRestaurants =
                    json.length === pageSize;

                this.loading = false;
                this.paginationLoading = false;
            }))
            .catch(action(error => {
                console.error('Unable to load restaurant data:', error);

                this.loading = false;
                this.paginationLoading = false;
                this.handleErrors(error);
            }));
    }

    @action loadMoreRestaurants() {
        if (
            this.paginationLoading ||
            !this.hasMoreRestaurants
        ) {
            return;
        }

        return this.getRestaurantListData(
            this.restaurantSearchMode,
            this.activeRestaurantSearch,
            true
        );
    }

    @action mergeRestaurants(existingRestaurants, newRestaurants) {
        const restaurantsById = new Map();

        existingRestaurants.forEach(restaurant => {
            restaurantsById.set(restaurant.id, {
                ...restaurant,
                violations: [...restaurant.violations]
            });
        });

        newRestaurants.forEach(restaurant => {
            if (!restaurantsById.has(restaurant.id)) {
                restaurantsById.set(restaurant.id, restaurant);
                return;
            }

            const existing = restaurantsById.get(restaurant.id);

            const violations = [
                ...existing.violations,
                ...restaurant.violations
            ];

            const uniqueViolations = violations.filter(
                (violation, index, allViolations) => {
                    return allViolations.findIndex(candidate => {
                        return (
                            candidate.violation_date ===
                            violation.violation_date &&
                            candidate.violation_type ===
                            violation.violation_type &&
                            candidate.violation_description ===
                            violation.violation_description &&
                            candidate.violation_points ===
                            violation.violation_points
                        );
                    }) === index;
                }
            );

            restaurantsById.set(restaurant.id, {
                ...existing,
                ...restaurant,
                violations: uniqueViolations.sort(
                    (a, b) =>
                        new Date(a.violation_date) -
                        new Date(b.violation_date)
                )
            });
        });

        return Array.from(restaurantsById.values())
            .sort(
                (a, b) =>
                    b.violations.length -
                    a.violations.length
            );
    }

    @action getMapObject(map) {
        const restaurant = this.selectedRestaurant;

        if (!map || !restaurant) return;

        this.mapObj =
            map.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;

        const placesService =
            new google.maps.places.PlacesService(this.mapObj);

        const geocoder = new google.maps.Geocoder();

        const fullAddress = [
            restaurant.address,
            restaurant.city,
            'WA',
            restaurant.zip_code
        ]
            .filter(Boolean)
            .join(', ');

        const geocodeRestaurant = () => {
            return new Promise((resolve, reject) => {
                geocoder.geocode(
                    {
                        address: fullAddress,
                        componentRestrictions: {
                            country: 'US'
                        }
                    },
                    (results, status) => {
                        if (
                            status === google.maps.GeocoderStatus.OK &&
                            results &&
                            results.length
                        ) {
                            resolve(results[0]);
                        } else {
                            reject(status);
                        }
                    }
                );
            });
        };

        const nearbySearch = location => {
            const request = {
                location,
                radius: 100,
                keyword: restaurant.name
            };

            return new Promise((resolve, reject) => {
                placesService.nearbySearch(
                    request,
                    (results, status) => {
                        if (
                            status ===
                            google.maps.places.PlacesServiceStatus.OK &&
                            results &&
                            results.length
                        ) {
                            resolve(results);
                        } else {
                            reject(status);
                        }
                    }
                );
            });
        };

        const findDetails = placeId => {
            return new Promise((resolve, reject) => {
                placesService.getDetails(
                    {
                        placeId,
                        fields: [
                            'name',
                            'formatted_address',
                            'opening_hours',
                            'reviews',
                            'rating'
                        ]
                    },
                    (details, status) => {
                        if (
                            status ===
                            google.maps.places.PlacesServiceStatus.OK
                        ) {
                            resolve(details);
                        } else {
                            reject(status);
                        }
                    }
                );
            });
        };

        const cleanRestaurantName = name => {
            return name
                .replace(/\bLLC\b/gi, '')
                .replace(/\bINC\b/gi, '')
                .replace(/\bCORP(?:ORATION)?\b/gi, '')
                .replace(/\bLTD\b/gi, '')
                .replace(/[#@]/g, ' ')
                .replace(/\s+/g, ' ')
                .replace(/[,\s]+$/, '')
                .trim();
        };

        const findPlaceFromQuery = query => {
            return new Promise((resolve, reject) => {
                placesService.findPlaceFromQuery(
                    {
                        query,
                        fields: [
                            'place_id',
                            'name',
                            'formatted_address',
                            'geometry'
                        ]
                    },
                    (results, status) => {
                        if (
                            status ===
                            google.maps.places.PlacesServiceStatus.OK &&
                            results &&
                            results.length
                        ) {
                            resolve(results[0]);
                        } else {
                            reject(status);
                        }
                    }
                );
            });
        };

        geocodeRestaurant()
            .then(geocodeResult => {
                const googleLocation =
                    geocodeResult.geometry.location;

                const location = {
                    lat: googleLocation.lat(),
                    lng: googleLocation.lng()
                };

                this.selectedRestaurant.lat = location.lat;
                this.selectedRestaurant.lng = location.lng;

                this.mapObj.setCenter(location);
                this.mapObj.setZoom(16);

                const cleanedName =
                    cleanRestaurantName(restaurant.name);

                const nameAndAddressQuery =
                    `${cleanedName}, ${fullAddress}`;

                return findPlaceFromQuery(nameAndAddressQuery)
                    .catch(() => nearbySearch(location))
                    .catch(() => findPlaceFromQuery(fullAddress))
                    .then(place => findDetails(place.place_id));
            })
            .then(details => {
                this.hours = details.opening_hours
                    ? details.opening_hours.weekday_text
                    : [];

                this.reviews = details.reviews || [];

                this.rating =
                    typeof details.rating === 'number'
                        ? details.rating
                        : null;
            })
            .catch(error => {
                if (
                    error !==
                    google.maps.places.PlacesServiceStatus.ZERO_RESULTS
                ) {
                    console.error(
                        'Google Places lookup failed:',
                        {
                            restaurant: restaurant.name,
                            address: fullAddress,
                            error
                        }
                    );
                }

                this.hours = [];
                this.reviews = [];
                this.rating = null;
            });
    }

    @action getRestaurantData(restaurant) {
        if(!restaurant) restaurant = this.selectedRestaurant;
        this.graphData = [];
        this.graphDataAllTime = [];
        this.violationTypeGraphData = [];
        this.violationTypeGraphDataAllTime = [];
        this.selectedRestaurant = restaurant;
        this.selectedRestaurantViolations = observable.map();
        this.selectedRestaurantViolationTypes = observable.map();
        this.selectedRestaurantViolationsAllTime = observable.map();
        this.selectedRestaurantViolationTypesAllTime = observable.map();

        const name = restaurant.name.replace(/["\\]/g, " ").replace(/[%&\/#]/g, (m) => {
            return "%" + m.charCodeAt(0).toString(16);
        });

        this.formatViolations(this.selectedRestaurant, this.selectedRestaurantViolations, this.selectedRestaurantViolationTypes);

        this.formatGraphData(this.graphData, this.violationTypeGraphData, this.selectedRestaurantViolations, this.selectedRestaurantViolationTypes);

        this.averagePointsPerInspection = this.graphData.map((d) => {
                return d.violation_points;
            }).reduce(( p, c ) => p + c, 0 ) / this.graphData.length;

        this.api.getRestaurantData(`$limit=50000&city=SEATTLE&name=${name}`)
            .then(checkStatus).then(response => response.json())
            .then((json) => {
                if(json.length) {
                    let data = this.filterData(json);
                    data = this.formatData(data);
                    this.selectedRestaurantAllTime = data[0];

                    this.formatViolations(this.selectedRestaurantAllTime, this.selectedRestaurantViolationsAllTime, this.selectedRestaurantViolationTypesAllTime);

                    this.formatGraphData(this.graphDataAllTime, this.violationTypeGraphDataAllTime, this.selectedRestaurantViolationsAllTime, this.selectedRestaurantViolationTypesAllTime);

                    this.averagePointsPerInspectionAllTime = this.graphDataAllTime.map((d) => {
                            return d.violation_points;
                        }).reduce((p, c) => p + c, 0) / this.graphDataAllTime.length;
                }
            }).catch(ex => this.handleErrors(ex))
    }

    @action resetSearchResults() {
        this.restaurantsSearchResults = null;
    }

    @action resetSelectedRestaurant() {
        this.selectedRestaurant = null;
    }

    @action search(query) {
        this.searchLoading = true;
        if(window.innerWidth <= 680 && this.showInfoWindow) this.toggleInfowindow();
        if(!query.length) {
            this.restaurantsSearchResults = this.restaurants
        } else {
            this.getRestaurantListData(true, query);
        }
        setTimeout(() => this.searchLoading = false, 1000)
    }

    @action setDateRange(range) {
        range = range || 12;
        return this.dateRange = moment().subtract(range, 'months').toISOString().slice(0,-1);
    }

    @action setPaginationPageNumber(page) {
        this.paginationLoading = true;
        this.pageNumber = page+1;
        setTimeout(() => this.paginationLoading = false, 2000)
    }

    @action showAllTimeData() {
        this.showAllData = !this.showAllData;
    }

    @action toggleInfowindow() {
        this.showInfoWindow = !this.showInfoWindow;
    }

    @action toggleLoading() {
        this.loading = !this.loading;
    }

    @action toggleModal(content) {
        if(this.openModal.has(content)) {
            this.openModal.delete(content)
        } else {
            this.openModal.set(content, true)
        }
    }

    @action toggleNestedList(id) {
        if(!this.openNestedListItems.has(id)) {
            this.openNestedListItems.set(id, true)
        } else {
            this.openNestedListItems.delete(id)
        }
    }

    @action toggleReviewList() {
        this.showReviews = !this.showReviews;
    }

    @action toggleSearch() {
        this.showSearch = !this.showSearch;
    }

    @action toggleSharing() {
        this.showSharingIcons = !this.showSharingIcons;
    }

    @action handleErrors(error) {
        this.loading = false;
        if (error && error.response && error.response.status) {
            if (error.response.status === 401) {
                window.location.href = window.location.protocol + '//' + window.location.host + '/#/login';
            }
        }
    }

}

const mainStore = new MainStore();

export default mainStore;
